import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatHalaman } from './halaman';

/** Konversi halaman ke juz: juz 30 = 23 hal, juz lainnya = 20 hal. */
function pagesInJuz(juzNum, pages) {
  const pph = Number(juzNum) === 30 ? 23 : 20;
  return (Number(pages) || 0) / pph;
}

/** Format desimal juz: 1 angka di belakang koma, tampilkan "X.X Juz". */
function fmtJuz(juz) {
  if (!juz || juz <= 0) return '0 Juz';
  return `${Math.round(juz * 10) / 10} Juz`;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function getLogoBase64() {
  try {
    const resp = await fetch('/logo-zahbaq.png');
    const blob = await resp.blob();
    return await blobToBase64(blob);
  } catch {
    return null;
  }
}

const KELAS_MAP = { D: "I'dad", C: 'Uula', B: 'Wustha', A: 'Ulya', M: 'Ali/Mahir' };

function materiText(s) {
  if (s.juz != null && s.juz !== '') return `Juz ${s.juz}`;
  return s.dari_surah_nama || s.surah_nama || '';
}
function rentangText(s) {
  if (s.juz != null && s.juz !== '' && s.dari_halaman != null) return `Hal ${s.dari_halaman}-${s.sampai_halaman ?? s.dari_halaman}`;
  if (s.dari_ayat != null) return `${s.dari_ayat}-${s.sampai_ayat ?? s.dari_ayat}`;
  if (s.ayat_awal != null) return `${s.ayat_awal}-${s.ayat_akhir}`;
  return '-';
}

function avgPenilaian(setoran) {
  const vals = setoran.map(s => s.nilai_total).filter(v => v != null);
  if (!vals.length) return null;
  const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  const huruf = avg >= 90 ? 'A' : avg >= 75 ? 'B' : avg >= 60 ? 'C' : 'D';
  return { avg, huruf };
}

export async function generateHafalanPDF(santri, setoran, progress, settings = {}) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const logoB64 = await getLogoBase64();

  // Header background
  doc.setFillColor(16, 185, 129);
  doc.rect(0, 0, 210, 38, 'F');

  // Logo
  if (logoB64) {
    doc.addImage(logoB64, 'PNG', 12, 5, 26, 26);
  } else {
    doc.setFillColor(6, 95, 70);
    doc.roundedRect(12, 7, 24, 24, 4, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Z', 24, 22, { align: 'center' });
  }

  // Header text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('ZahBaq', 42, 17);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Pesantren Baitul Quran · Dabo Singkep, Kabupaten Lingga', 42, 24);
  const ta = settings.tahun_ajaran ? `Tahun Ajaran ${settings.tahun_ajaran} · Semester ${settings.semester || 'Ganjil'}` : '';
  if (ta) doc.text(ta, 42, 31);

  // Title card
  doc.setFillColor(240, 253, 244);
  doc.rect(15, 45, 180, 14, 'F');
  doc.setDrawColor(16, 185, 129);
  doc.setLineWidth(0.5);
  doc.rect(15, 45, 180, 14);
  doc.setTextColor(22, 101, 52);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('KARTU HAFALAN AL-QURAN', 105, 54, { align: 'center' });

  // Santri info box (4 baris kiri + 3 kanan)
  const pnlSntrn = avgPenilaian(setoran);
  const kelasLabel = KELAS_MAP[santri.kelas] || santri.kelas || '-';
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(220, 220, 220);
  doc.roundedRect(15, 65, 180, 42, 2, 2, 'FD');
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  const infoY = 73;
  doc.text('Nama Santri:', 22, infoY);
  doc.text('NIS:', 22, infoY + 8);
  doc.text('Kelas:', 22, infoY + 16);
  doc.text('Rata-rata Nilai:', 22, infoY + 24);
  doc.text('Total Setoran:', 115, infoY);
  doc.text('Total Halaman:', 115, infoY + 8);
  doc.text('Juz Ziyadah:', 115, infoY + 16);

  doc.setFont('helvetica', 'normal');
  doc.text(santri.name || '-', 58, infoY);
  doc.text(santri.nis || '-', 58, infoY + 8);
  doc.text(`${santri.kelas || '-'} - ${kelasLabel}`, 58, infoY + 16);
  if (pnlSntrn) {
    doc.setTextColor(22, 101, 52);
    doc.setFont('helvetica', 'bold');
    doc.text(`${pnlSntrn.avg} (Grade ${pnlSntrn.huruf})`, 58, infoY + 24);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
  } else {
    doc.text('-', 58, infoY + 24);
  }
  doc.text(`${progress?.total_setoran || 0} kali`, 155, infoY);
  doc.text(`${formatHalaman(progress?.total_pages || 0)} halaman`, 155, infoY + 8);
  doc.text(`${progress?.juz_completed || 0} dari 30 Juz`, 155, infoY + 16);

  const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(`Tanggal cetak: ${dateStr}`, 115, infoY + 24);

  // Table
  autoTable(doc, {
    startY: 115,
    head: [['#', 'Juz / Materi', 'Rentang', 'Hal', 'Jenis', 'Kelas', 'Nilai', 'Tgl Setoran', 'Catatan Guru']],
    body: setoranRows(setoran, santri.kelas),
    theme: 'striped',
    headStyles: {
      fillColor: [16, 185, 129], textColor: [255, 255, 255],
      fontSize: 7.5, fontStyle: 'bold', halign: 'center'
    },
    alternateRowStyles: { fillColor: [240, 253, 244] },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [200, 230, 220], lineWidth: 0.2 },
    columnStyles: {
      0: { cellWidth: 7, halign: 'center' },
      1: { cellWidth: 24 },
      2: { cellWidth: 14, halign: 'center' },
      3: { cellWidth: 10, halign: 'center' },
      4: { cellWidth: 18 },
      5: { cellWidth: 14 },
      6: { cellWidth: 11, halign: 'center' },
      7: { cellWidth: 20, halign: 'center' },
    }
  });

  // Signature area
  const tableEnd = doc.lastAutoTable.finalY + 10;
  if (tableEnd < 250) {
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text('Mengetahui,', 22, tableEnd);
    doc.text('Guru Pembimbing', 22, tableEnd + 6);
    doc.text('Wali Santri', 110, tableEnd);
    doc.setLineWidth(0.3);
    doc.setDrawColor(100, 100, 100);
    doc.line(22, tableEnd + 30, 80, tableEnd + 30);
    doc.line(110, tableEnd + 30, 168, tableEnd + 30);
  }

  // Footer on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageH = doc.internal.pageSize.height;
    doc.setDrawColor(16, 185, 129);
    doc.setLineWidth(0.3);
    doc.line(15, pageH - 18, 195, pageH - 18);
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Halaman ${i} dari ${pageCount}`, 15, pageH - 12);
    doc.text('ZahBaq · Sistem Manajemen Tahfiz Pesantren Baitul Quran', 105, pageH - 12, { align: 'center' });
    doc.setTextColor(22, 101, 52);
    doc.text(`Dicetak: ${dateStr}`, 195, pageH - 12, { align: 'right' });
  }

  doc.save(`Kartu-Hafalan-${santri.name}-${new Date().toISOString().split('T')[0]}.pdf`);
}

// ==================== REKAP PERKEMBANGAN PDF ====================
export async function generateRekapPDF(santri, setoran, absensi, tasmiList, progress, settings = {}) {
  const doc = new jsPDF('l', 'mm', 'a4'); // landscape
  const W = doc.internal.pageSize.getWidth();   // 297
  const H = doc.internal.pageSize.getHeight();  // 210
  const M = 14; // margin
  const logoB64 = await getLogoBase64();
  const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const BULAN_ID = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

  // ── Computed stats ──────────────────────────────────────
  const totalZiyadahJuz   = setoran.filter(x => x.jenis === 'ziyadah').reduce((acc, x) => acc + pagesInJuz(x.juz, x.jml_halaman), 0);
  const totalBinnadzarJuz = setoran.filter(x => ['bin_nadzar','bin_nadhar'].includes(x.jenis)).reduce((acc, x) => acc + pagesInJuz(x.juz, x.jml_halaman), 0);
  const progresBulanan = setoran.filter(x => x.jenis === 'ziyadah' && x.tanggal?.startsWith(thisMonth)).reduce((acc, x) => acc+(x.jml_halaman||0), 0);
  const absH  = absensi.filter(x => x.status==='H').length;
  const absI  = absensi.filter(x => x.status==='I').length;
  const absS  = absensi.filter(x => x.status==='S').length;
  const absA  = absensi.filter(x => x.status==='A').length;
  const absTS = absensi.filter(x => x.status==='TS').length;
  const grade = tasmiList.length
    ? (tasmiList[0].penilaian || (tasmiList[0].keterangan === 'Remedial' ? 'Remedial' : tasmiList[0].keterangan ? 'A' : '-'))
    : '-';
  const kelasLabel = KELAS_MAP[santri.kelas] || santri.kelas || '-';
  const ta = settings.tahun_ajaran ? `TA ${settings.tahun_ajaran} · Sem ${settings.semester || 'Ganjil'}` : '';

  // ── Header bar ──────────────────────────────────────────
  doc.setFillColor(4, 64, 40);
  doc.rect(0, 0, W, 22, 'F');
  if (logoB64) { try { doc.addImage(logoB64, 'PNG', M, 3, 14, 14); } catch {} }
  doc.setFont('helvetica','bold'); doc.setFontSize(13); doc.setTextColor(255,255,255);
  doc.text('ZahBaq', M + 17, 12);
  doc.setFont('helvetica','normal'); doc.setFontSize(8);
  doc.text('Pesantren Baitul Quran · Dabo Singkep', M + 17, 18);
  doc.setFontSize(13); doc.setFont('helvetica','bold');
  doc.text('REKAP PERKEMBANGAN SANTRI', W/2, 13, { align: 'center' });
  if (ta) { doc.setFontSize(8); doc.setFont('helvetica','normal'); doc.text(ta, W/2, 19, { align: 'center' }); }
  doc.setFontSize(8); doc.text(`Dicetak: ${dateStr}`, W - M, 15, { align: 'right' });

  // ── Santri info box ─────────────────────────────────────
  let y = 28;
  doc.setFillColor(240, 253, 244); doc.setDrawColor(16, 185, 129); doc.setLineWidth(0.4);
  doc.roundedRect(M, y, W - M*2, 18, 2, 2, 'FD');
  doc.setTextColor(22,101,52); doc.setFontSize(9); doc.setFont('helvetica','bold');
  doc.text(santri.name || '-', M + 5, y + 7);
  doc.setFont('helvetica','normal'); doc.setFontSize(8); doc.setTextColor(60,60,60);
  const infoLine = [
    santri.nis ? `NIS: ${santri.nis}` : null,
    santri.kelas ? `Kelas: ${santri.kelas} – ${kelasLabel}` : null,
    santri.halaqah_nama ? `Halaqah: ${santri.halaqah_nama}` : null,
  ].filter(Boolean).join('   ·   ');
  doc.text(infoLine, M + 5, y + 13);
  doc.setTextColor(22,101,52); doc.setFont('helvetica','bold'); doc.setFontSize(9);
  doc.text(`Grade: ${grade}`, W - M - 5, y + 7, { align: 'right' });
  doc.setFont('helvetica','normal'); doc.setFontSize(8); doc.setTextColor(100,100,100);
  doc.text(`Juz Selesai (BQ): ${progress?.juz_completed || 0} / 30`, W - M - 5, y + 13, { align: 'right' });

  // ── Tabel Rekap Utama (1 baris) ─────────────────────────
  y = 52;
  autoTable(doc, {
    startY: y,
    head: [['Nama Santri', 'Grade', 'H', 'I', 'S', 'A', 'TS', 'Total Ziyadah', 'Total Binnadzar', 'Progres Bulan Ini']],
    body: [[
      santri.name || '-',
      grade,
      absH, absI, absS, absA, absTS,
      fmtJuz(totalZiyadahJuz),
      fmtJuz(totalBinnadzarJuz),
      `${formatHalaman(progresBulanan)} hal`,
    ]],
    theme: 'striped',
    headStyles: { fillColor: [16, 185, 129], textColor: [255,255,255], fontSize: 8, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 9, halign: 'center' },
    columnStyles: {
      0: { cellWidth: 50, halign: 'left' },
      1: { cellWidth: 18 },
      2: { cellWidth: 12 }, 3: { cellWidth: 12 }, 4: { cellWidth: 12 }, 5: { cellWidth: 12 }, 6: { cellWidth: 12 },
      7: { cellWidth: 30 }, 8: { cellWidth: 30 }, 9: { cellWidth: 32 },
    },
  });

  // ── Tabel Progres Bulanan ───────────────────────────────
  // Group by month
  const monthSet = new Set([
    ...setoran.map(s => s.tanggal?.slice(0,7)).filter(Boolean),
    ...absensi.map(a => a.tanggal?.slice(0,7)).filter(Boolean),
  ]);
  const months = [...monthSet].sort().reverse().slice(0, 12);

  if (months.length > 0) {
    const tblY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9); doc.setFont('helvetica','bold'); doc.setTextColor(22,101,52);
    doc.text('Progres per Bulan', M, tblY - 2);

    const monthRows = months.map(m => {
      const [yr, mn] = m.split('-');
      const bulanLabel = `${BULAN_ID[parseInt(mn)-1]} ${yr}`;
      const zHal = setoran.filter(x => x.jenis==='ziyadah' && x.tanggal?.startsWith(m)).reduce((acc, x) => acc+(x.jml_halaman||0), 0);
      const bHal = setoran.filter(x => ['bin_nadzar','bin_nadhar'].includes(x.jenis) && x.tanggal?.startsWith(m)).reduce((acc, x) => acc+(x.jml_halaman||0), 0);
      const cntH  = absensi.filter(x => x.status==='H'  && x.tanggal?.startsWith(m)).length;
      const cntI  = absensi.filter(x => x.status==='I'  && x.tanggal?.startsWith(m)).length;
      const cntS  = absensi.filter(x => x.status==='S'  && x.tanggal?.startsWith(m)).length;
      const cntA  = absensi.filter(x => x.status==='A'  && x.tanggal?.startsWith(m)).length;
      const cntTS = absensi.filter(x => x.status==='TS' && x.tanggal?.startsWith(m)).length;
      return [bulanLabel, `${formatHalaman(zHal)} hal`, `${formatHalaman(bHal)} hal`, cntH, cntI, cntS, cntA, cntTS];
    });

    autoTable(doc, {
      startY: tblY,
      head: [['Bulan', 'Ziyadah', 'Binnadzar', 'H', 'I', 'S', 'A', 'TS']],
      body: monthRows,
      theme: 'striped',
      headStyles: { fillColor: [5, 150, 105], textColor: [255,255,255], fontSize: 8, fontStyle: 'bold', halign: 'center' },
      bodyStyles: { fontSize: 8.5, halign: 'center' },
      alternateRowStyles: { fillColor: [240, 253, 244] },
      columnStyles: {
        0: { cellWidth: 45, halign: 'left' },
        1: { cellWidth: 35 }, 2: { cellWidth: 35 },
        3: { cellWidth: 20 }, 4: { cellWidth: 20 }, 5: { cellWidth: 20 }, 6: { cellWidth: 20 }, 7: { cellWidth: 20 },
      },
    });
  }

  // ── Footer ──────────────────────────────────────────────
  const pgCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pgCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(16, 185, 129); doc.setLineWidth(0.3);
    doc.line(M, H - 14, W - M, H - 14);
    doc.setFontSize(7); doc.setTextColor(150,150,150);
    doc.text(`Halaman ${i} dari ${pgCount}`, M, H - 8);
    doc.text('ZahBaq · Sistem Manajemen Tahfiz Pesantren Baitul Quran', W/2, H - 8, { align: 'center' });
    doc.setTextColor(22,101,52);
    doc.text(`Dicetak: ${dateStr}`, W - M, H - 8, { align: 'right' });
  }

  doc.save(`Rekap-Perkembangan-${santri.name}-${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function generateHafalanPNG(santri, setoran, progress, settings = {}) {
  const W = 794, PAD = 20;
  const rowH = 18, tableRows = Math.min(setoran.length, 18);
  const H = 76 + 32 + 70 + 16 + 28 + tableRows * rowH + 80 + 36;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  const fill = (x, y, w, h, color) => { ctx.fillStyle = color; ctx.fillRect(x, y, w, h); };
  const txt = (str, x, y, { color = '#1f2937', size = 9, bold = false, align = 'left' } = {}) => {
    ctx.fillStyle = color; ctx.font = `${bold ? 'bold ' : ''}${size}px Arial`;
    ctx.textAlign = align; ctx.fillText(String(str), x, y); ctx.textAlign = 'left';
  };
  const line = (x1, y1, x2, y2, color = '#d1fae5') => {
    ctx.strokeStyle = color; ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  };

  // Background
  fill(0, 0, W, H, '#ffffff');

  // Header emerald
  fill(0, 0, W, 70, '#10b981');
  txt('ZahBaq', 55, 30, { color: '#fff', size: 20, bold: true });
  txt('Pesantren Baitul Quran · Dabo Singkep, Kabupaten Lingga', 55, 46, { color: '#d1fae5', size: 9 });
  const ta = settings.tahun_ajaran ? `Tahun Ajaran ${settings.tahun_ajaran} · Semester ${settings.semester || 'Ganjil'}` : '';
  if (ta) txt(ta, 55, 60, { color: '#a7f3d0', size: 8 });

  // Title bar
  fill(PAD, 76, W - PAD * 2, 26, '#f0fdf4');
  ctx.strokeStyle = '#10b981'; ctx.lineWidth = 0.5; ctx.strokeRect(PAD, 76, W - PAD * 2, 26);
  txt('KARTU HAFALAN AL-QURAN', W / 2, 94, { color: '#166534', size: 11, bold: true, align: 'center' });

  // Santri info box (with penilaian avg)
  const pngPnl = avgPenilaian(setoran);
  const pngKelasLabel = KELAS_MAP[santri.kelas] || (santri.kelas || '-');
  let infoY = 108;
  ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 0.5; ctx.strokeRect(PAD, infoY, W - PAD * 2, 68);
  const cols2 = [
    ['Nama Santri', santri.name || '-'],
    ['NIS', santri.nis || '-'],
    ['Kelas', `${santri.kelas || '-'} - ${pngKelasLabel}`],
    ['Rata-rata Nilai', pngPnl ? `${pngPnl.avg} (Grade ${pngPnl.huruf})` : '-'],
  ];
  const cols2R = [
    ['Total Setoran', `${progress?.total_setoran || 0} kali`],
    ["Total Halaman", `${formatHalaman(progress?.total_pages || 0)} hal`],
    ['Juz Ziyadah', `${progress?.juz_completed || 0}/30`],
  ];
  cols2.forEach(([label, val], i) => {
    txt(label + ' :', PAD + 8, infoY + 16 + i * 14, { color: '#374151', size: 8, bold: true });
    txt(val, PAD + 92, infoY + 16 + i * 14, { color: i === 3 ? '#059669' : '#374151', size: 8 });
  });
  cols2R.forEach(([label, val], i) => {
    txt(label + ' :', W / 2 + 10, infoY + 16 + i * 16, { color: '#374151', size: 8, bold: true });
    txt(val, W / 2 + 105, infoY + 16 + i * 16, { color: '#374151', size: 8 });
  });

  const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  txt(`Dicetak: ${dateStr}`, PAD + 8, infoY + 62, { color: '#9ca3af', size: 7 });

  // Table
  const tableY = infoY + 76;
  const tcols = [
    { w: 22, label: '#', align: 'center' },
    { w: 80, label: 'Juz / Materi', align: 'left' },
    { w: 56, label: 'Rentang', align: 'center' },
    { w: 32, label: 'Hal', align: 'center' },
    { w: 68, label: 'Jenis', align: 'left' },
    { w: 58, label: 'Kelas', align: 'left' },
    { w: 42, label: 'Nilai', align: 'center' },
    { w: 78, label: 'Tanggal', align: 'center' },
  ];
  const fixedW = tcols.reduce((s, c) => s + c.w, 0);
  tcols.push({ w: W - PAD * 2 - fixedW, label: 'Catatan', align: 'left' });

  // Table header
  fill(PAD, tableY, W - PAD * 2, 22, '#10b981');
  let cx = PAD;
  tcols.forEach(col => {
    ctx.fillStyle = '#fff'; ctx.font = 'bold 7.5px Arial';
    ctx.textAlign = col.align === 'center' ? 'center' : 'left';
    ctx.fillText(col.label, col.align === 'center' ? cx + col.w / 2 : cx + 4, tableY + 14);
    ctx.textAlign = 'left'; cx += col.w;
  });

  // Table rows
  setoran.slice(0, 15).forEach((s, i) => {
    const ry = tableY + 22 + i * rowH;
    fill(PAD, ry, W - PAD * 2, rowH, i % 2 === 0 ? '#f0fdf4' : '#fff');
    const kelasLbl = KELAS_MAP[santri.kelas] || (santri.kelas || '-');
    const jenis = s.jenis === 'ziyadah' ? 'Ziyadah' : s.jenis === 'murajaah' ? 'Murajaah' : s.jenis === 'bin_nadhar' ? 'Bin Nadzar' : s.jenis === 'tasmi' ? "Tasmi'" : s.jenis === 'iqro' ? `Iqro J.${s.iqro_jilid || ''}` : (s.jenis || '-');
    const cells = [
      { text: String(i + 1), align: 'center' },
      { text: (materiText(s) || jenis).substring(0, 11), align: 'left' },
      { text: rentangText(s), align: 'center' },
      { text: formatHalaman(s.jml_halaman || 0), align: "center" },
      { text: jenis, align: 'left' },
      { text: kelasLbl, align: 'left' },
      { text: s.nilai_total != null ? String(Math.round(s.nilai_total)) : '-', align: 'center' },
      { text: s.tanggal || '-', align: 'center' },
      { text: (s.catatan || '').substring(0, 20), align: 'left' },
    ];
    let cx2 = PAD;
    cells.forEach((cell, ci) => {
      ctx.fillStyle = '#374151'; ctx.font = '6.5px Arial';
      ctx.textAlign = cell.align === 'center' ? 'center' : 'left';
      ctx.fillText(cell.text, cell.align === 'center' ? cx2 + tcols[ci].w / 2 : cx2 + 3, ry + 11);
      ctx.textAlign = 'left'; cx2 += tcols[ci].w;
    });
  });

  const tableEnd = tableY + 22 + tableRows * rowH;
  ctx.strokeStyle = '#d1fae5'; ctx.lineWidth = 0.3; ctx.strokeRect(PAD, tableY, W - PAD * 2, 22 + tableRows * rowH);

  // Signature
  const sigY = tableEnd + 16;
  txt('Mengetahui,', PAD + 8, sigY, { color: '#374151', size: 8 });
  txt('Guru Pembimbing', PAD + 8, sigY + 12, { color: '#374151', size: 8 });
  txt('Orang Tua / Wali', W / 2 + 20, sigY, { color: '#374151', size: 8 });
  line(PAD + 8, sigY + 44, PAD + 150, sigY + 44, '#9ca3af');
  line(W / 2 + 20, sigY + 44, W / 2 + 160, sigY + 44, '#9ca3af');

  // Footer
  const footY = H - 14;
  line(PAD, footY - 8, W - PAD, footY - 8, '#10b981');
  txt('ZahBaq · Sistem Manajemen Tahfiz Pesantren Baitul Quran', W / 2, footY, { color: '#9ca3af', size: 7, align: 'center' });
  txt(`Dicetak: ${dateStr}`, W - PAD, footY, { color: '#059669', size: 7, align: 'right' });

  // Download
  const a = document.createElement('a');
  a.download = `Kartu-Hafalan-${santri.name}-${new Date().toISOString().split('T')[0]}.png`;
  a.href = canvas.toDataURL('image/png');
  a.click();
}


function setoranRows(setoran, santriKelas = '') {
  const kelasLabel = KELAS_MAP[santriKelas] || (santriKelas || '-');
  return setoran.slice(0, 50).map((s, i) => {
    const jenis = s.jenis === 'ziyadah' ? 'Ziyadah'
      : s.jenis === 'murajaah' ? 'Murajaah'
      : s.jenis === 'bin_nadhar' ? 'Bin Nadzar'
      : s.jenis === 'tasmi' ? "Tasmi'"
      : s.jenis === 'iqro' ? `Iqro J.${s.iqro_jilid || ''}`
      : (s.jenis || '-');
    const materi = materiText(s) || jenis;
    const rentang = rentangText(s);
    const nilaiNum = s.nilai_total != null ? Math.round(s.nilai_total) : '-';
    const tgl = s.tanggal || '-';
    return [i + 1, materi, rentang, formatHalaman(s.jml_halaman || 0), jenis, kelasLabel, nilaiNum, tgl, (s.catatan || '').substring(0, 25)];
  });
}

// ==================== KARTU TASMI' PDF (LANDSCAPE) ====================
const BULAN_PDF = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'];

function formatTglPDF(tgl) {
  if (!tgl) return '-';
  const d = new Date(tgl);
  if (isNaN(d)) return tgl;
  return `${d.getDate()} ${BULAN_PDF[d.getMonth()]} ${d.getFullYear()}`;
}

function formatJuzPDF(t) {
  if (t.tipe === 'multi') {
    return `Juz ${t.juz_dari}\u2013${t.juz_sampai}\n(${t.jumlah_juz} Juz)`;
  }
  return `Juz ${t.juz ?? '-'}`;
}

function formatDurasiPDF(t) {
  if (!t.jam_mulai && !t.jam_selesai) return '-';
  return `${t.jam_mulai || '?'} \u2013 ${t.jam_selesai || '?'} WIB`;
}

export async function generateTasmiPDF(santri, tasmiList, settings = {}) {
  const doc = new jsPDF('l', 'mm', 'a4'); // landscape
  const W = doc.internal.pageSize.getWidth();   // 297
  const H = doc.internal.pageSize.getHeight();  // 210
  const MARGIN = 12;
  const usable = W - MARGIN * 2;

  // Fetch logo
  let logoB64 = null;
  try { logoB64 = await getLogoBase64(); } catch {}

  // ── Header bar ─────────────────────────────────────────
  doc.setFillColor(4, 64, 40); // emerald-950
  doc.rect(0, 0, W, 24, 'F');

  if (logoB64) {
    try { doc.addImage(logoB64, 'PNG', MARGIN, 3, 16, 16); } catch {}
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text("KARTU TASMI' TAHFIZH AL-QUR'AN", W / 2, 11, { align: 'center' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(settings.nama_pesantren || 'Pesantren Tahfizh', W / 2, 18, { align: 'center' });

  // ── Santri info box ────────────────────────────────────
  let y = 30;
  const infoRows = [
    ['Nama Santri', santri.name || '-'],
    ['NIS', santri.nis || '-'],
    ['Kelas', santri.kelas ? `${santri.kelas} \u2013 ${KELAS_MAP[santri.kelas] || ''}` : '-'],
    ['Halaqah', santri.halaqah_nama || '-'],
  ];

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(30, 30, 30);

  const colW = usable / 2;
  infoRows.forEach((row, i) => {
    const col = i % 2;
    const rowY = y + Math.floor(i / 2) * 7;
    const x = MARGIN + col * colW;
    doc.setFont('helvetica', 'bold');
    doc.text(`${row[0]}`, x, rowY);
    doc.setFont('helvetica', 'normal');
    doc.text(`: ${row[1]}`, x + 30, rowY);
  });

  y += Math.ceil(infoRows.length / 2) * 7 + 4;

  // ── Thin separator ─────────────────────────────────────
  doc.setDrawColor(180, 180, 180);
  doc.line(MARGIN, y, W - MARGIN, y);
  y += 4;

  // ── Tasmi' table ───────────────────────────────────────
  const cols = [
    { header: 'No',          dataKey: 'no'  },
    { header: 'Juz',         dataKey: 'juz' },
    { header: 'Durasi',      dataKey: 'durasi' },
    { header: 'Tanggal',     dataKey: 'tgl' },
    { header: 'Tempat Tasmi\'', dataKey: 'tempat' },
    { header: 'Penyimak',    dataKey: 'penyimak' },
    { header: 'Jml Jali',    dataKey: 'jali' },
    { header: 'Nilai',       dataKey: 'ket' },
  ];

  const rows = tasmiList.map((t, i) => ({
    no: i + 1,
    juz: formatJuzPDF(t),
    durasi: formatDurasiPDF(t),
    tgl: formatTglPDF(t.tanggal),
    tempat: (t.tempat || '-').substring(0, 25),
    penyimak: (t.penyimak || '-').substring(0, 20),
    jali: t.jumlah_jali ?? 0,
    ket: t.penilaian || (t.keterangan === 'Remedial' ? 'Remedial' : (t.keterangan ? 'A' : '-')),
  }));

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    columns: cols,
    body: rows,
    styles: { fontSize: 7.5, cellPadding: 2.5, valign: 'middle' },
    headStyles: { fillColor: [4, 64, 40], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: [240, 253, 244] },
    columnStyles: {
      no:      { cellWidth: 8,  halign: 'center' },
      juz:     { cellWidth: 26 },
      durasi:  { cellWidth: 36 },
      tgl:     { cellWidth: 26 },
      tempat:  { cellWidth: 'auto' },
      penyimak:{ cellWidth: 'auto' },
      jali:    { cellWidth: 16, halign: 'center' },
      ket:     { cellWidth: 26, halign: 'center' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.dataKey === 'ket') {
        const val = data.cell.raw;
        if (val === 'Remedial') data.cell.styles.textColor = [180, 30, 30];
        else data.cell.styles.textColor = [5, 101, 55];
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // ── Footer ─────────────────────────────────────────────
  const finalY = Math.min(doc.lastAutoTable.finalY + 8, H - 10);
  doc.setFontSize(7);
  doc.setTextColor(120, 120, 120);
  doc.setFont('helvetica', 'italic');
  doc.text(
    `Dicetak: ${new Date().toLocaleDateString('id-ID', { day:'2-digit', month:'long', year:'numeric' })}`,
    MARGIN, finalY
  );
  doc.text('ZahBaq \u2013 Sistem Manajemen Tahfizh', W - MARGIN, finalY, { align: 'right' });

  doc.save(`Kartu-Tasmi-${santri.name}-${new Date().toISOString().split('T')[0]}.pdf`);
}
